import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { brl, pct } from '../../lib/format'
import { exportAudit } from '../../lib/exports'
import { color, radius, shadow } from '../../theme/tokens'
import type { CallState } from '../../lib/callEngine'
import type { MatrixRow, PolicyVersion } from '../../lib/types'
import { Button } from '../ui'

const GUARD_ICONS = {
  ok: { icon: '✓', bg: color.greenTintSoft, fg: color.greenInk },
  wait: { icon: '○', bg: color.surfaceMuted, fg: color.inkFaint },
  warn: { icon: '⚠', bg: color.amberTint, fg: color.amberInk },
} as const

type Tab = 'tools' | 'perf' | 'audit'

/**
 * Modo Auditor.
 *
 * Painel deslizante com o raciocínio por turno, a política aplicada, os
 * guardrails avaliados, as ferramentas executadas, a latência e a trilha
 * append-only. É a resposta ao "prove o que a máquina fez".
 */
export function AuditorBand({
  open,
  height,
  onHeightChange,
  onCollapse,
  state,
  matrix,
  appliedLine,
  policyVersion,
  publishedAt,
  publishedBy,
  latency,
}: {
  open: boolean
  height: number
  onHeightChange(next: number): void
  onCollapse(): void
  state: CallState
  matrix: MatrixRow[]
  appliedLine: string
  policyVersion: string
  publishedAt: string
  publishedBy: PolicyVersion['published_by']
  latency: { step: string; ms: number }[]
}) {
  const [tab, setTab] = useState<Tab>('tools')
  const [openTools, setOpenTools] = useState<Record<string, boolean>>({})
  const dragOrigin = useRef<{ y: number; height: number } | null>(null)

  const startDrag = useCallback(
    (event: React.MouseEvent) => {
      dragOrigin.current = { y: event.clientY, height }
      const move = (moveEvent: MouseEvent) => {
        const origin = dragOrigin.current
        if (!origin) return
        onHeightChange(Math.max(160, Math.min(620, origin.height + (origin.y - moveEvent.clientY))))
      }
      const up = () => {
        dragOrigin.current = null
        window.removeEventListener('mousemove', move)
        window.removeEventListener('mouseup', up)
      }
      window.addEventListener('mousemove', move)
      window.addEventListener('mouseup', up)
    },
    [height, onHeightChange],
  )

  const totalLatency = latency.reduce((sum, item) => sum + item.ms, 0)
  const e2e = `${totalLatency + state.jitterMs}ms`

  return (
    <section
      aria-label="Modo auditor"
      aria-hidden={!open}
      style={{
        position: 'absolute',
        left: 60,
        right: 0,
        bottom: 0,
        zIndex: 35,
        background: color.surface,
        borderTop: `1px solid ${color.borderDashed}`,
        boxShadow: shadow.band,
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform .42s cubic-bezier(.22,.61,.36,1)',
        height,
        transform: open ? 'translateY(0)' : 'translateY(101%)',
      }}
    >
      <div
        onMouseDown={startDrag}
        role="separator"
        aria-orientation="horizontal"
        aria-label="Redimensionar painel do auditor"
        style={{
          height: 14,
          flex: 'none',
          cursor: 'ns-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f7f8fa',
          borderBottom: '1px solid #eceef1',
        }}
      >
        <span style={{ width: 44, height: 3, borderRadius: 2, background: color.inkGhost }} />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '9px 18px',
          borderBottom: '1px solid #eceef1',
          flex: 'none',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: color.ink }}>
          MODO AUDITOR
        </span>
        <span style={{ fontSize: 11, color: color.inkSubtle }}>
          Política v{policyVersion} · publicada em {publishedAt} por {publishedBy} ·{' '}
          <Link to="/politica">ver na configuração</Link>
        </span>
        <div style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: color.greenInk,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: color.green }} />
          trilha append-only ativa
        </span>
        <Button onClick={onCollapse} style={{ padding: '6px 12px', fontSize: 12 }}>
          Recolher
        </Button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* 1 · Raciocínio ao vivo */}
        <div
          className="dk"
          style={{
            flex: 1.35,
            minWidth: 0,
            overflowY: 'auto',
            padding: '14px 16px',
            borderRight: '1px solid #eceef1',
          }}
        >
          <ColumnTitle>1 · RACIOCÍNIO AO VIVO</ColumnTitle>
          {state.reasoning.length === 0 ? (
            <div style={{ fontSize: 12, color: color.inkFaint }}>
              Os cards de raciocínio aparecem a cada turno da IA.
            </div>
          ) : null}
          {state.reasoning.map((card) => (
            <div
              key={`${card.turn}-${card.at}`}
              style={{
                border: `1px solid ${color.border}`,
                borderRadius: radius.xl,
                padding: '12px 14px',
                marginBottom: 10,
                animation: 'mat .34s ease both',
                background: color.surface,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 9,
                }}
              >
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: color.inkSubtle,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  TURNO {card.turn} · {card.at}
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    padding: '3px 8px',
                    borderRadius: radius.pill,
                    background: color.blueTint,
                    color: color.blueInk,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  confiança {card.confidence}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 7,
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <div>
                  <b>Intenção detectada:</b> {card.intent}
                </div>
                <div>
                  <b>Sentimento:</b> {card.sentiment}
                </div>
                <div>
                  <b>Contexto recuperado:</b>{' '}
                  <span style={{ color: color.blueInk }}>{card.retrieved_context}</span>
                </div>
                <div>
                  <b>Estratégia escolhida:</b> {card.strategy}
                </div>
                <div
                  style={{
                    padding: '8px 10px',
                    borderRadius: radius.md,
                    background: color.redTint,
                    border: `1px solid ${color.redBorder}`,
                    color: color.redDeep,
                  }}
                >
                  <b>Descartado:</b> {card.discarded}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 2 · Política aplicada e 3 · Guardrails */}
        <div
          className="dk"
          style={{
            flex: 1,
            minWidth: 0,
            overflowY: 'auto',
            padding: '14px 16px',
            borderRight: '1px solid #eceef1',
          }}
        >
          <ColumnTitle>2 · POLÍTICA APLICADA</ColumnTitle>
          <div
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.xl,
              overflow: 'hidden',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1.1fr .9fr .7fr .7fr',
                background: color.brandTint,
                fontSize: 10,
                fontWeight: 700,
                color: color.brandInk,
                letterSpacing: '.04em',
              }}
            >
              <div style={{ padding: '7px 9px' }}>SEGMENTO</div>
              <div style={{ padding: '7px 9px' }}>ATRASO</div>
              <div style={{ padding: '7px 9px' }}>À VISTA</div>
              <div style={{ padding: '7px 9px' }}>ENT+PARC</div>
            </div>
            {matrix.map((row) => (
              <div
                key={`${row.segment}-${row.bucket}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.1fr .9fr .7fr .7fr',
                  fontSize: row.applied ? 12 : 11.5,
                  fontWeight: row.applied ? 700 : 400,
                  background: row.applied ? color.greenTint : color.surface,
                  color: row.applied ? color.greenInk : color.inkSubtle,
                  borderTop: `1px solid ${row.applied ? color.greenBorder : color.borderSoft}`,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <div style={{ padding: '8px 9px' }}>{row.segment}</div>
                <div style={{ padding: '8px 9px' }}>{row.bucket}</div>
                <div style={{ padding: '8px 9px' }}>{pct(row.cash_cap)}</div>
                <div style={{ padding: '8px 9px' }}>{pct(row.installment_cap)}</div>
              </div>
            ))}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: color.greenInk,
              margin: '0 0 16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              lineHeight: 1.45,
            }}
          >
            <span aria-hidden>✓</span>
            Linha aplicada ao caso em demonstração · {appliedLine}
          </div>

          <ColumnTitle>3 · GUARDRAILS AVALIADOS</ColumnTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {state.guardrails.map((guardrail) => {
              const tint = GUARD_ICONS[guardrail.state]
              return (
                <div
                  key={guardrail.key}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    fontSize: 12,
                    lineHeight: 1.45,
                    padding: '7px 9px',
                    borderRadius: radius.md,
                    transition: 'background .3s ease',
                    background: tint.bg,
                    color: tint.fg,
                  }}
                >
                  <span style={{ fontWeight: 700, flex: 'none', width: 14 }} aria-hidden>
                    {tint.icon}
                  </span>
                  <span>{guardrail.label}</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* 4/5/6 · Ferramentas, desempenho, trilha */}
        <div style={{ flex: 1.05, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 4, padding: '10px 14px 0', flex: 'none' }}>
            <TabButton active={tab === 'tools'} onClick={() => setTab('tools')}>
              4 · Ferramentas
            </TabButton>
            <TabButton active={tab === 'perf'} onClick={() => setTab('perf')}>
              5 · Desempenho
            </TabButton>
            <TabButton active={tab === 'audit'} onClick={() => setTab('audit')}>
              6 · Trilha
            </TabButton>
          </div>

          <div
            className="dk"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              padding: '12px 14px',
              borderTop: '1px solid #eceef1',
            }}
          >
            {tab === 'tools' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {state.toolCalls.length === 0 ? (
                  <div style={{ fontSize: 12, color: color.inkFaint }}>
                    Nenhuma ferramenta executada até agora.
                  </div>
                ) : null}
                {state.toolCalls.map((call) => {
                  const expanded = Boolean(openTools[call.id])
                  return (
                    <div
                      key={call.id}
                      style={{
                        border: `1px solid ${color.border}`,
                        borderRadius: radius.lg,
                        overflow: 'hidden',
                        animation: 'mat .3s ease both',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenTools((current) => ({ ...current, [call.id]: !current[call.id] }))
                        }
                        aria-expanded={expanded}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '9px 11px',
                          cursor: 'pointer',
                          background: color.surfaceMuted,
                          border: 'none',
                          width: '100%',
                          textAlign: 'left',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "'Roboto Mono', monospace",
                            fontSize: 11.5,
                            fontWeight: 500,
                            color: color.blueInk,
                            flex: 1,
                          }}
                        >
                          {call.name}()
                        </span>
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: radius.pill,
                            background: color.greenTint,
                            color: color.greenInk,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {call.result}
                        </span>
                        <span style={{ fontSize: 11, color: color.inkSubtle }} aria-hidden>
                          {expanded ? '▾' : '▸'}
                        </span>
                      </button>
                      {expanded ? (
                        <pre
                          style={{
                            margin: 0,
                            padding: '10px 12px',
                            background: color.panel,
                            color: color.panelText,
                            fontFamily: "'Roboto Mono', monospace",
                            fontSize: 10.5,
                            lineHeight: 1.6,
                            overflowX: 'auto',
                          }}
                        >
                          {JSON.stringify(call.arguments, null, 2)}
                        </pre>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ) : null}

            {tab === 'perf' ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                  <span
                    style={{
                      fontSize: 26,
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      letterSpacing: '-.5px',
                    }}
                  >
                    {e2e}
                  </span>
                  <span style={{ fontSize: 11.5, color: color.inkSubtle }}>ponta a ponta</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {latency.map((item) => (
                    <div key={item.step}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 11.5,
                          marginBottom: 4,
                        }}
                      >
                        <span>{item.step}</span>
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {item.ms}ms
                        </span>
                      </div>
                      <div
                        style={{
                          height: 5,
                          borderRadius: 3,
                          background: color.bg,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            background: color.blue,
                            transition: 'width .4s ease',
                            width: `${Math.round((item.ms / 260) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    marginTop: 14,
                    paddingTop: 12,
                    borderTop: `1px dashed ${color.border}`,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: color.inkSubtle }}>Barge-in bem-sucedido</div>
                    <div
                      style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                    >
                      94%
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: color.inkSubtle }}>Custo da chamada</div>
                    <div
                      style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {brl(state.costBrl)}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === 'audit' ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Button
                    onClick={() => exportAudit(state, policyVersion, 'json')}
                    style={{ padding: '6px 11px', fontSize: 11.5 }}
                  >
                    Exportar JSON
                  </Button>
                  <Button
                    onClick={() => exportAudit(state, policyVersion, 'csv')}
                    style={{ padding: '6px 11px', fontSize: 11.5 }}
                  >
                    Exportar CSV
                  </Button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[...state.audit].reverse().map((entry, index) => (
                    <div
                      key={`${entry.hash}-${index}`}
                      style={{
                        display: 'flex',
                        gap: 9,
                        padding: '7px 8px',
                        borderRadius: radius.sm,
                        background: color.surfaceMuted,
                        fontSize: 11,
                        alignItems: 'baseline',
                      }}
                    >
                      <span
                        style={{
                          fontVariantNumeric: 'tabular-nums',
                          color: color.inkSubtle,
                          flex: 'none',
                          width: 38,
                        }}
                      >
                        {entry.at}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>{entry.event}</span>
                      <span
                        style={{
                          fontFamily: "'Roboto Mono', monospace",
                          fontSize: 10,
                          color: color.inkFaint,
                          flex: 'none',
                        }}
                      >
                        {entry.hash}
                      </span>
                    </div>
                  ))}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: color.inkFaint,
                    marginTop: 10,
                    lineHeight: 1.5,
                  }}
                >
                  Áudio retido por 90 dias · transcrição por 5 anos · PII mascarada no log conforme
                  LGPD.
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}

function ColumnTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.07em',
        color: color.inkSubtle,
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick(): void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        border: 'none',
        borderRadius: '8px 8px 0 0',
        padding: '8px 12px',
        fontSize: 11.5,
        fontWeight: 700,
        cursor: 'pointer',
        background: active ? color.surface : '#f2f4f7',
        color: active ? color.brandHover : color.inkSubtle,
      }}
    >
      {children}
    </button>
  )
}
